import axios from "axios";
import { ApplicationApiEndpoint } from "../utils/Constants";
import { StrongFoundationService } from "./StrongFoundationService";
import { User } from "../models/User";
import { ProfileOverviewResponse } from "../models/ProfileOverview";

export interface UserLoginResponse {
    token: string;
    refreshToken: string;
    id: string;
}

export interface UserRegistrationData {
    name: string;
    username: string;
    password: string;
    dob: number;
}

export class UserService extends StrongFoundationService {
    /**
     * Authenticate with username + password. Passport local strategy handles
     * credential verification server-side — no Bearer token required.
     */
    public login = async (
        username: string,
        password: string,
    ): Promise<UserLoginResponse | null> => {
        try {
            const response = await axios.post<UserLoginResponse>(
                `${ApplicationApiEndpoint}user/login`,
                { username, password },
            );
            return response.data ?? null;
        } catch {
            return null;
        }
    };

    /**
     * Register a new user account. Requires an anonymous Bearer token
     * (handled automatically by StrongFoundationService).
     */
    public register = async (
        data: UserRegistrationData,
    ): Promise<UserLoginResponse | null> => {
        try {
            const result = await this.post<UserLoginResponse>("user/register", data);
            return result ?? null;
        } catch {
            return null;
        }
    };

    /**
     * Check if a username is already taken before registration.
     */
    public isUsernameTaken = async (username: string): Promise<boolean> => {
        try {
            const result = await this.get<{ isTaken: boolean }>("user/usernameTaken", {
                username,
            });
            return result?.isTaken ?? false;
        } catch {
            return false;
        }
    };

    public getUser = async (id: string): Promise<User | null> => {
        try {
            const user = await this.get<User>("user", { id });
            return user ?? null;
        } catch {
            return null;
        }
    };

    /**
     * Fetch the profile overview payload for a user.
     * Includes profile stats (posts/recipes/workouts/routines) and preview content.
     */
    public getUserOverview = async (id: string): Promise<ProfileOverviewResponse | null> => {
        try {
            const overview = await this.get<ProfileOverviewResponse>(`user/${id}/overview`);
            return overview ?? null;
        } catch {
            return null;
        }
    };

    public searchUsersByName = async (query: string): Promise<User[]> => {
        if (!query.trim()) {
            return [];
        }
        try {
            const users = await this.get<User[]>("user/search", { q: query.trim() });
            return users ?? [];
        } catch {
            return [];
        }
    };

    /**
     * Update user fields (profile, cover, etc).
     */
    public update = async (fields: Partial<User>): Promise<User | null> => {
        try {
            const updated = await this.put<User>("user", fields);
            return updated ?? null;
        } catch {
            return null;
        }
    };
}

export const userService = new UserService();
